import { beforeUpdate, getDefinitions } from '~/admin/definitions';
import { api } from '~/api';
import type { Filter, PaginatedResponse, Sort } from '~/search';
import type {
    CreateCustomerExpenseRequest,
    DomainListResponse,
    Expense,
    UpdateCustomerExpenseRequest,
} from '~/types/platform';
import { defaultSort, getSingle, searchTable } from './api.new';

type Row = Record<string, unknown>;
type BackofficeRelation = { table?: string; field?: string };
type BackofficeProperty = { type?: string | string[]; relation?: BackofficeRelation };
type BackofficeSchema = { properties: Record<string, BackofficeProperty> };
type SchemaTableResponse = { schema: BackofficeSchema } & Record<string, unknown>;
type RelatedEntities = Record<string, { table: SchemaTableResponse; rows: Row[] }>;
const isRow = (value: unknown): value is Row => typeof value === 'object' && value !== null;

export async function getTables(): Promise<DomainListResponse> {
    return api<DomainListResponse>('/admin/backoffice/');
}

export function searchUsers(
    input: string,
    roles: string[] = [],
    customer_id = 0,
    companyOwners = false,
): Promise<any[]> {
    return api<any[]>('/admin/users/search', {
        method: 'POST',
        body: {
            input,
            roles,
            customer_id: customer_id ? customer_id : null,
            company_owners: companyOwners,
        },
    });
}

export async function talentUsers(): Promise<any[]> {
    return (await api<any[]>('/admin/users/talent')) || [];
}

export function searchCandidates(
    input: string,
    roles: string[] = [],
    withUser = false,
): Promise<any[]> {
    return api<any[]>('/admin/candidates/find', {
        method: 'POST',
        body: { input, roles, with_user: withUser },
    });
}

export function search<T = Row>(
    table: string,
    input: string,
    opts?: Record<string, any>,
): Promise<T[]> {
    const defs = getDefinitions(table, null);
    if (defs.customSearch) {
        return defs.customSearch(input, opts) as Promise<T[]>;
    }

    const filters = defs.search?.(input, opts) || [];
    return searchTable<Row>({ table, page: 1, filters }).then((data) => (data.rows || []) as T[]);
}

export function preloadAll(table: string, opts?: Record<string, any>): Promise<Row[]> {
    const defs = getDefinitions(table, null);
    const filters = defs.preload ? defs.preload(opts) : [];
    return searchTable<Row>({ table, page: 1, perPage: 99999, filters }).then(
        (data) => data.rows || [],
    );
}

type ImpersonateResponse = { token: string | null; expires_at: string | number | null };

export function impersonate(id: number): Promise<ImpersonateResponse> {
    return api<ImpersonateResponse>(`/admin/impersonate/${id}`, { method: 'POST' });
}

export async function getRecord<T extends Row = Row>(
    table: string,
    id: number | string,
): Promise<any> {
    const [schema, data] = await Promise.all([getSchema(table), getSingle<T>(table, Number(id))]);

    return {
        schema: schema.schema,
        data,
    };
}

export async function getRecordByField<T extends Row = Row>(
    table: string,
    field: string,
    value: unknown,
): Promise<any> {
    const [schema, data] = await Promise.all([
        getSchema(table),
        searchTable<T>({
            table,
            filters: [{ field, value, operator: 'equals' }],
        }),
    ]);

    const rows = data.rows || [];

    return {
        schema: schema.schema,
        data: rows.length > 0 ? rows[0] : {},
    };
}

export async function create(table: string, data: Row): Promise<Row> {
    return await api<Row>(`/admin/backoffice/${table}/`, {
        method: 'POST',
        body: beforeUpdate(table, data),
    });
}

export async function update(table: string, data: Row & { id: number }): Promise<Row> {
    return await api<Row>(`/admin/backoffice/${table}/${data.id}`, {
        method: 'PATCH',
        body: beforeUpdate(table, data),
    });
}

export async function remove(table: string, id: number): Promise<boolean> {
    return await api<boolean>(`/admin/backoffice/${table}/${id}`, { method: 'DELETE' });
}

export function getSchema(table: string): Promise<SchemaTableResponse> {
    return api<SchemaTableResponse>(`/admin/backoffice/${table}/schema`);
}

export async function exportData({
    table,
    filters,
    sort = defaultSort,
}: {
    table: string;
    filters: Filter[];
    sort?: Sort[];
}): Promise<Blob> {
    return await api
        .raw(`/admin/backoffice/${table}/export`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                filters,
                sort,
            }),
        })
        .then((r) => r.blob());
}

export async function listData<T extends Row = Row>({
    table,
    page,
    perPage,
    filters = [],
    sort,
}: {
    table: string;
    page?: number;
    perPage?: number;
    filters?: Filter[];
    sort?: Sort[];
}): Promise<any> {
    const [schema, search] = await Promise.all([
        getSchema(table),
        searchTable<T>({ table, page, perPage, filters, sort }),
    ]);

    return { ...search, table: schema };
}

export async function getRelated(
    record: Row,
    table: string,
    field: string,
    parentField = 'id',
    additionalFilters: Filter[] = [],
): Promise<any> {
    const val = record[parentField];
    const filters: Filter[] = [];
    if (Array.isArray(val)) {
        for (const v of val) {
            filters.push({ type: 'OR', operator: 'equals', field, value: v });
        }
    } else {
        filters.push({ type: 'AND', operator: 'equals', field, value: val });
    }

    filters.push(...additionalFilters);

    const [schema, search] = await Promise.all([
        getSchema(table),
        filters.length > 0 ? searchTable<Row>({ table, filters }) : Promise.resolve({ rows: [] }),
    ]);

    return { table: schema, results: search.rows || [] };
}

export async function uploadDocument(table: string, file: File): Promise<any> {
    const data = new FormData();
    data.append('file', file);
    return api<any>(`/admin/upload/${table}`, {
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'multipart/form-data' },
    });
}

export async function uploadPublicDocument(type: string, file: File): Promise<any> {
    const data = new FormData();
    data.append('file', file);
    return api<any>(`/admin/upload/public_document/${type}`, {
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'multipart/form-data' },
    });
}

export async function uploadPicture(file: File): Promise<any> {
    return uploadPublicDocument('pictures', file);
}

export async function selectRelated(
    table: string,
    id: number,
    field: string,
    relatedId: number,
): Promise<Row> {
    const { data, schema } = await getRecord(table, id);
    const schemaField = schema.properties[field];
    const fieldType = schemaField?.type;
    const typeList = Array.isArray(fieldType) ? fieldType : [fieldType];
    const isArray = typeList.filter(Boolean).includes('array');
    if (isArray) {
        const existing = Array.isArray(data[field]) ? data[field] : [];
        data[field] = [...existing, relatedId];
    } else {
        data[field] = relatedId;
    }

    return await update(table, data as { id: number } & Row);
}

export async function getRelatedEntities(
    schema: BackofficeSchema,
    rows: Row[] | null | undefined,
    fields: string[] = [],
): Promise<RelatedEntities> {
    if (!rows) return {};
    const result: RelatedEntities = {};
    for (const k of Object.keys(schema.properties)) {
        const rel = schema.properties[k]?.relation;
        if (!rel || !rel.table || !rel.field) continue;
        if (fields.length > 0 && fields.indexOf(k) < 0) continue;

        const filters: Filter[] = [];
        for (const r of rows) {
            if (r[k]) {
                if (Array.isArray(r[k])) {
                    for (const v of r[k] as unknown[]) {
                        filters.push({
                            type: 'OR',
                            field: rel.field,
                            operator: 'equals',
                            value: v,
                        });
                    }
                } else {
                    filters.push({
                        type: 'OR',
                        field: rel.field,
                        operator: 'equals',
                        value: r[k],
                    });
                }
            }
        }

        if (filters.length > 0) {
            const { rows: relatedRows, table: relatedTable } = await listData({
                table: rel.table,
                filters,
                perPage: filters.length,
            });
            const relatedTableSchema = relatedTable.schema as BackofficeSchema;
            const rawRelatedRows: unknown[] = Array.isArray(relatedRows) ? relatedRows : [];
            const relatedRowsList: Row[] = rawRelatedRows.filter(isRow);

            const defs = getDefinitions(rel.table, relatedTableSchema);
            if (defs.expand) {
                const expandFields: string[] = Array.isArray(defs.list?.fields)
                    ? defs.list.fields
                    : [];
                const related = await getRelatedEntities(
                    relatedTableSchema,
                    relatedRowsList,
                    expandFields,
                );
                expandRelatedEntity(relatedRowsList, related, relatedTableSchema);
            }

            if (!result[rel.table]) {
                result[rel.table] = { table: relatedTable, rows: [] };
            }
            for (const row of relatedRowsList) {
                result[rel.table].rows.push(row);
            }
        }
    }

    return result;
}

function expandRelatedEntity(
    rows: Row[],
    related: RelatedEntities,
    schema: BackofficeSchema,
): void {
    for (const k of Object.keys(schema.properties)) {
        const rel = schema.properties[k]?.relation;
        if (!rel || !rel.table || !rel.field) continue;

        for (const r of rows) {
            if (r[k] && related[rel.table]) {
                r[rel.table] = related[rel.table].rows.find(
                    (x: Row) => x[rel.field as string] === r[k],
                );
            }
        }
    }
}

export async function getAllCustomers(): Promise<Row[]> {
    let page = 1;
    const result: Row[] = [];
    for (;;) {
        const resp = await searchTable<Row>({
            table: 'customers',
            page: page++,
            perPage: 50,
            filters: [],
        });
        const customers = resp.rows || [];
        result.push(...customers);
        if (customers.length < 50) break;
    }

    return result;
}

export function searchDeposits({
    filters,
    page = 1,
    perPage = 50,
    sort,
}: {
    filters?: Filter[];
    page?: number;
    perPage?: number;
    sort?: Sort[];
}): Promise<PaginatedResponse<any>> {
    return api<PaginatedResponse<any>>('/admin/deposits/search', {
        method: 'POST',
        body: { filters: filters ?? [], sort: sort ?? [], page, per_page: perPage },
    });
}

export function getInvoiceLineInvoice(id: number): Promise<any> {
    return api<any>(`/admin/invoice_lines/${id}/invoice`);
}

export function getExpenses(
    {
        filters,
        sort: outterSort,
    }: {
        filters: Filter[];
        sort: Sort | Sort[];
    },
    page = 1,
    perPage = 50,
): Promise<PaginatedResponse<Expense>> {
    const sort = Array.isArray(outterSort) ? outterSort : [outterSort];

    for (const s of sort) {
        if (s.field === 'expense') {
            s.field = 'title';
        }
    }

    return api<PaginatedResponse<Expense>>('/admin/customers/expenses/search', {
        method: 'POST',
        body: {
            filters,
            sort,
            page: Number(page),
            per_page: perPage,
        },
    });
}

export function createExpense(data: CreateCustomerExpenseRequest): Promise<Expense> {
    return api<Expense>(`/admin/customers/expenses`, {
        method: 'POST',
        body: data,
    });
}

export function updateExpenseStatus(
    id: number | string,
    status: UpdateCustomerExpenseRequest['status'],
): Promise<any> {
    return api<any>(`/admin/customers/expenses/${id}`, {
        method: 'PATCH',
        body: { status },
    });
}
